import React, { useState, useEffect } from 'react';
import { sewaGedungService } from '../../../services/sewaGedungService';
import { useAuth } from '../../Admin/context/AuthContext';
import { getAssetUrl } from '../../../services/api';
import {
    FaSearch, FaFilter, FaEye, FaCheckCircle, FaTimesCircle,
    FaTrash, FaFilePdf, FaImage, FaDownload, FaSpinner,
    FaExclamationTriangle, FaChevronLeft, FaChevronRight,
    FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt, FaCalendarAlt,
    FaFileAlt, FaClock, FaCheck, FaBan, FaInfoCircle,
    FaMoneyBillWave, FaUpload, FaTimes, FaPaperPlane,
    FaBuilding, FaUsers, FaMapMarkedAlt, FaEdit
} from 'react-icons/fa';

const statusConfig = {
    baru: { label: 'Baru', color: 'bg-blue-100 text-blue-700', icon: FaPaperPlane },
    diproses: { label: 'Diproses', color: 'bg-yellow-100 text-yellow-700', icon: FaClock },
    disetujui: { label: 'Disetujui', color: 'bg-green-100 text-green-700', icon: FaCheckCircle },
    ditolak: { label: 'Ditolak', color: 'bg-red-100 text-red-700', icon: FaTimesCircle },
};

const paymentStatusConfig = {
    belum_bayar: { label: 'Belum Bayar', color: 'bg-gray-100 text-gray-600' },
    menunggu_verifikasi: { label: 'Menunggu Verifikasi', color: 'bg-yellow-100 text-yellow-700' },
    sudah_bayar: { label: 'Sudah Bayar', color: 'bg-green-100 text-green-700' },
    gagal: { label: 'Gagal', color: 'bg-red-100 text-red-700' },
};

const ManajemenPermohonanSewaGedung = () => {
    const { user } = useAuth();
    const hasPermission = (p) => {
        if (!user) return false;
        if (user?.roles?.includes('admin')) return true;
        return user?.permissions?.includes(p) || false;
    };

    // Data
    const [submissions, setSubmissions] = useState([]);
    const [statistics, setStatistics] = useState({});
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Filter
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterPayment, setFilterPayment] = useState('');

    // Detail modal
    const [detailModal, setDetailModal] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Payment modal
    const [paymentModal, setPaymentModal] = useState(null);
    const [paymentForm, setPaymentForm] = useState({
        total_biaya: '',
        payment_status: 'menunggu_verifikasi',
        payment_date: new Date().toISOString().split('T')[0],
        payment_note: '',
        payment_proof: null,
    });
    const [submitting, setSubmitting] = useState(false);

    // Status modal
    const [statusModal, setStatusModal] = useState(null);
    const [statusAction, setStatusAction] = useState('diproses');
    const [statusCatatan, setStatusCatatan] = useState('');

    // Delete
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Notification
    const [notification, setNotification] = useState(null);

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    // ============ LOAD DATA ============
    const loadData = async (page = 1) => {
        setLoading(true);
        const params = { page };
        if (searchTerm) params.search = searchTerm;
        if (filterStatus) params.status = filterStatus;
        if (filterPayment) params.payment_status = filterPayment;

        const [listRes, statRes] = await Promise.all([
            sewaGedungService.getSubmissions(params),
            sewaGedungService.getSubmissionStatistics(),
        ]);

        if (listRes.success) {
            setSubmissions(listRes.data?.data || []);
            setTotalPages(listRes.data?.last_page || 1);
            setCurrentPage(listRes.data?.current_page || 1);
        }
        if (statRes.success) {
            setStatistics(statRes.data || {});
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        loadData(1);
    };

    // ============ DETAIL ============
    const openDetail = async (id) => {
        setDetailLoading(true);
        setDetailModal({ id, loading: true });
        const result = await sewaGedungService.getSubmissionDetail(id);
        if (result.success) {
            setDetailModal(result.data);
        } else {
            showNotif(result.message, 'error');
            setDetailModal(null);
        }
        setDetailLoading(false);
    };

    // ============ PAYMENT MODAL ============
    const openPaymentModal = (sub) => {
        setPaymentModal(sub);
        setPaymentForm({
            total_biaya: sub.total_biaya || '',
            payment_status: sub.payment_status || 'menunggu_verifikasi',
            payment_date: sub.payment_date || new Date().toISOString().split('T')[0],
            payment_note: sub.payment_note || '',
            payment_proof: null,
        });
    };

    const handlePaymentProofChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            showNotif('File maksimal 5MB', 'error');
            return;
        }

        const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        if (!allowed.includes(file.type)) {
            showNotif('Format file harus JPG, PNG, atau PDF', 'error');
            return;
        }

        setPaymentForm(prev => ({ ...prev, payment_proof: file }));
    };

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();
        if (!paymentForm.total_biaya || parseFloat(paymentForm.total_biaya) < 0) {
            showNotif('Total biaya wajib diisi', 'error');
            return;
        }

        setSubmitting(true);

        const fd = new FormData();
        fd.append('total_biaya', paymentForm.total_biaya);
        fd.append('payment_status', paymentForm.payment_status);
        fd.append('payment_date', paymentForm.payment_date || '');
        fd.append('payment_note', paymentForm.payment_note || '');
        if (paymentForm.payment_proof) {
            fd.append('payment_proof', paymentForm.payment_proof);
        }

        const result = await sewaGedungService.confirmPayment(paymentModal.id, fd);
        if (result.success) {
            showNotif(result.message || 'Pembayaran berhasil dikonfirmasi');
            setPaymentModal(null);
            loadData(currentPage);
            // Refresh detail jika sedang dibuka
            if (detailModal?.id === paymentModal.id) {
                openDetail(paymentModal.id);
            }
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    // ============ STATUS MODAL ============
    const openStatusModal = (sub, action = 'diproses') => {
        setStatusModal(sub);
        setStatusAction(action);
        setStatusCatatan('');
    };

    const handleUpdateStatus = async () => {
        if (!statusModal) return;
        setSubmitting(true);

        const result = await sewaGedungService.updateSubmissionStatus(statusModal.id, {
            status: statusAction,
            catatan_admin: statusCatatan,
        });

        if (result.success) {
            showNotif(result.message || 'Status berhasil diubah');
            setStatusModal(null);
            loadData(currentPage);
            if (detailModal?.id === statusModal.id) {
                openDetail(statusModal.id);
            }
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    // ============ DELETE ============
    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const result = await sewaGedungService.deleteSubmission(deleteConfirm.id);
        if (result.success) {
            showNotif('Data berhasil dihapus');
            loadData(currentPage);
        } else {
            showNotif(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    // ============ HELPERS ============
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('id-ID', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const formatTanggal = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric', month: 'short', year: 'numeric',
        });
    };

    const formatRupiah = (angka) => {
        if (!angka && angka !== 0) return '-';
        return new Intl.NumberFormat('id-ID', {
            style: 'currency', currency: 'IDR', minimumFractionDigits: 0,
        }).format(angka);
    };

    const getFileUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return getAssetUrl(path);
    };

    const getFileName = (path) => path?.split('/').pop() || 'file';

    const getFileIcon = (path) => {
        const ext = path?.split('.').pop()?.toLowerCase();
        if (ext === 'pdf') return <FaFilePdf className="text-red-500" />;
        if (['jpg', 'jpeg', 'png'].includes(ext)) return <FaImage className="text-blue-500" />;
        return <FaFileAlt className="text-gray-500" />;
    };

    const StatusBadge = ({ status }) => {
        const config = statusConfig[status] || statusConfig.baru;
        const Icon = config.icon;
        return (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
                <Icon className="mr-1" size={10} /> {config.label}
            </span>
        );
    };

    const PaymentBadge = ({ status }) => {
        const config = paymentStatusConfig[status] || paymentStatusConfig.belum_bayar;
        return (
            <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
                {config.label}
            </span>
        );
    };

    return (
        <div>
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-[#0F4C3A]">Permohonan Sewa Gedung</h2>
                <p className="text-gray-600">Review, verifikasi, dan konfirmasi pembayaran permohonan sewa</p>
            </div>

            {/* Notification */}
            {notification && (
                <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success' ? <FaCheckCircle className="mr-2" /> : <FaExclamationTriangle className="mr-2" />}
                    {notification.message}
                </div>
            )}

            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                {[
                    { label: 'Total', value: statistics.total, color: 'text-[#0F4C3A]' },
                    { label: 'Baru', value: statistics.baru, color: 'text-blue-600' },
                    { label: 'Diproses', value: statistics.diproses, color: 'text-yellow-600' },
                    { label: 'Disetujui', value: statistics.disetujui, color: 'text-green-600' },
                    { label: 'Ditolak', value: statistics.ditolak, color: 'text-red-600' },
                    { label: 'Hari Ini', value: statistics.hari_ini, color: 'text-purple-600' },
                ].map((stat, i) => (
                    <div key={i} className="bg-white rounded-lg p-3 shadow-sm text-center">
                        <p className={`text-2xl font-bold ${stat.color}`}>{stat.value || 0}</p>
                        <p className="text-xs text-gray-500">{stat.label}</p>
                    </div>
                ))}
            </div>

            {/* Filter */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
                <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Cari nama, NIK, atau nomor registrasi..."
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                        />
                    </div>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                    >
                        <option value="">Semua Status</option>
                        <option value="baru">Baru</option>
                        <option value="diproses">Diproses</option>
                        <option value="disetujui">Disetujui</option>
                        <option value="ditolak">Ditolak</option>
                    </select>
                    <select
                        value={filterPayment}
                        onChange={(e) => setFilterPayment(e.target.value)}
                        className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                    >
                        <option value="">Semua Pembayaran</option>
                        <option value="belum_bayar">Belum Bayar</option>
                        <option value="menunggu_verifikasi">Menunggu Verifikasi</option>
                        <option value="sudah_bayar">Sudah Bayar</option>
                        <option value="gagal">Gagal</option>
                    </select>
                    <button type="submit" className="btn-primary flex items-center justify-center">
                        <FaFilter className="mr-2" /> Filter
                    </button>
                </form>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-sm text-gray-500">
                                <th className="p-4 font-medium">No. Registrasi</th>
                                <th className="p-4 font-medium">Pemohon</th>
                                <th className="p-4 font-medium">Gedung</th>
                                <th className="p-4 font-medium">Tanggal Sewa</th>
                                <th className="p-4 font-medium">Total Biaya</th>
                                <th className="p-4 font-medium">Status</th>
                                <th className="p-4 font-medium">Pembayaran</th>
                                <th className="p-4 font-medium text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="p-8 text-center">
                                        <FaSpinner className="animate-spin text-2xl text-[#2E7D32] mx-auto" />
                                    </td>
                                </tr>
                            ) : submissions.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="p-8 text-center text-gray-500">
                                        Belum ada permohonan
                                    </td>
                                </tr>
                            ) : (
                                submissions.map((sub) => (
                                    <tr key={sub.id} className="border-b hover:bg-gray-50 transition-colors">
                                        <td className="p-4">
                                            <p className="font-mono text-xs font-semibold text-[#0F4C3A]">
                                                {sub.nomor_registrasi}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                {formatTanggal(sub.created_at)}
                                            </p>
                                        </td>
                                        <td className="p-4">
                                            <p className="font-medium text-gray-700">{sub.nama_lengkap}</p>
                                            <p className="text-xs text-gray-500">NIK: {sub.nik}</p>
                                            <p className="text-xs text-gray-500">{sub.no_telp}</p>
                                        </td>
                                        <td className="p-4">
                                            <p className="text-sm text-gray-700">{sub.gedung?.nama || '-'}</p>
                                            <p className="text-xs text-gray-500">
                                                {sub.jumlah_peserta} peserta
                                            </p>
                                        </td>
                                        <td className="p-4 text-sm text-gray-600">
                                            <p>{formatTanggal(sub.tanggal_mulai)}</p>
                                            {sub.tanggal_selesai !== sub.tanggal_mulai && (
                                                <p className="text-xs text-gray-400">
                                                    s/d {formatTanggal(sub.tanggal_selesai)}
                                                </p>
                                            )}
                                            <p className="text-xs text-gray-500 mt-1">
                                                {sub.jam_mulai?.slice(0, 5)} - {sub.jam_selesai?.slice(0, 5)}
                                            </p>
                                        </td>
                                        <td className="p-4">
                                            {sub.total_biaya ? (
                                                <p className="font-semibold text-[#0F4C3A]">
                                                    {formatRupiah(sub.total_biaya)}
                                                </p>
                                            ) : (
                                                <span className="text-xs text-gray-400 italic">Belum di-set</span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <StatusBadge status={sub.status} />
                                        </td>
                                        <td className="p-4">
                                            <PaymentBadge status={sub.payment_status} />
                                        </td>
                                        <td className="p-4">
                                            <div className="flex justify-center gap-1">
                                                <button
                                                    onClick={() => openDetail(sub.id)}
                                                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                                    title="Detail"
                                                >
                                                    <FaEye size={14} />
                                                </button>
                                                {hasPermission('sewa-gedung.update') && (
                                                    <button
                                                        onClick={() => openPaymentModal(sub)}
                                                        className="p-2 text-green-500 hover:bg-green-50 rounded-lg"
                                                        title="Konfirmasi Pembayaran"
                                                    >
                                                        <FaMoneyBillWave size={14} />
                                                    </button>
                                                )}
                                                {hasPermission('sewa-gedung.delete') && (
                                                    <button
                                                        onClick={() => setDeleteConfirm(sub)}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                        title="Hapus"
                                                    >
                                                        <FaTrash size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                        <p className="text-sm text-gray-500">
                            Halaman {currentPage} dari {totalPages}
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => loadData(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                <FaChevronLeft size={12} />
                            </button>
                            <button
                                onClick={() => loadData(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                <FaChevronRight size={12} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ============ MODAL DETAIL ============ */}
            {detailModal && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-4xl w-full my-8 max-h-[calc(100vh-4rem)] flex flex-col shadow-2xl">
                        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white rounded-t-2xl z-10">
                            <div>
                                <h3 className="text-xl font-bold text-[#0F4C3A]">Detail Permohonan</h3>
                                <p className="text-sm text-gray-500 font-mono mt-1">
                                    {detailModal.nomor_registrasi}
                                </p>
                            </div>
                            <button onClick={() => setDetailModal(null)} className="text-gray-400 hover:text-gray-600">
                                <FaTimes size={20} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1 space-y-5">
                            {detailLoading ? (
                                <div className="text-center py-8">
                                    <FaSpinner className="animate-spin text-3xl text-[#2E7D32] mx-auto" />
                                </div>
                            ) : (
                                <>
                                    {/* Status cards */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-4 bg-gray-50 rounded-lg">
                                            <p className="text-xs text-gray-500 mb-2">Status Permohonan</p>
                                            <StatusBadge status={detailModal.status} />
                                        </div>
                                        <div className="p-4 bg-gray-50 rounded-lg">
                                            <p className="text-xs text-gray-500 mb-2">Status Pembayaran</p>
                                            <PaymentBadge status={detailModal.payment_status} />
                                        </div>
                                    </div>

                                    {/* Total biaya */}
                                    {detailModal.total_biaya && (
                                        <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                                            <div className="flex items-center justify-between flex-wrap gap-3">
                                                <div>
                                                    <p className="text-xs text-green-700 mb-1">Total Biaya</p>
                                                    <p className="text-2xl font-bold text-green-700">
                                                        {formatRupiah(detailModal.total_biaya)}
                                                    </p>
                                                </div>
                                                {hasPermission('sewa-gedung.update') && (
                                                    <button
                                                        onClick={() => openPaymentModal(detailModal)}
                                                        className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 flex items-center gap-2"
                                                    >
                                                        <FaMoneyBillWave size={12} /> Konfirmasi Pembayaran
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Data Pemohon */}
                                    <div>
                                        <h4 className="font-bold text-[#0F4C3A] mb-3 flex items-center">
                                            <FaUser className="mr-2 text-[#2E7D32]" /> Data Pemohon
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Nama Lengkap</p>
                                                <p className="text-sm font-semibold">{detailModal.nama_lengkap}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">NIK</p>
                                                <p className="text-sm font-semibold font-mono">{detailModal.nik}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Telepon</p>
                                                <p className="text-sm font-semibold">{detailModal.no_telp}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Email</p>
                                                <p className="text-sm font-semibold">{detailModal.email || '-'}</p>
                                            </div>
                                            <div className="md:col-span-2 p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Alamat</p>
                                                <p className="text-sm">{detailModal.alamat}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Pekerjaan</p>
                                                <p className="text-sm">{detailModal.pekerjaan || '-'}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Jenis Instansi</p>
                                                <p className="text-sm capitalize">{detailModal.jenis_instansi}</p>
                                                {detailModal.nama_instansi && (
                                                    <p className="text-xs text-gray-500">{detailModal.nama_instansi}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Data Sewa */}
                                    <div>
                                        <h4 className="font-bold text-[#0F4C3A] mb-3 flex items-center">
                                            <FaBuilding className="mr-2 text-[#2E7D32]" /> Data Sewa
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div className="md:col-span-2 p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Gedung</p>
                                                <p className="text-sm font-semibold">{detailModal.gedung?.nama || '-'}</p>
                                                <p className="text-xs text-gray-500">{detailModal.gedung?.lokasi}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Jenis Kegiatan</p>
                                                <p className="text-sm font-semibold">{detailModal.jenis_kegiatan}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Jumlah Peserta</p>
                                                <p className="text-sm font-semibold">{detailModal.jumlah_peserta} orang</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Tanggal Mulai</p>
                                                <p className="text-sm font-semibold">{formatTanggal(detailModal.tanggal_mulai)}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Tanggal Selesai</p>
                                                <p className="text-sm font-semibold">{formatTanggal(detailModal.tanggal_selesai)}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Jam Mulai</p>
                                                <p className="text-sm font-semibold">{detailModal.jam_mulai?.slice(0, 5)}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Jam Selesai</p>
                                                <p className="text-sm font-semibold">{detailModal.jam_selesai?.slice(0, 5)}</p>
                                            </div>
                                            <div className="md:col-span-2 p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Deskripsi Kegiatan</p>
                                                <p className="text-sm whitespace-pre-wrap">{detailModal.deskripsi_kegiatan}</p>
                                            </div>
                                            {Array.isArray(detailModal.fasilitas_tambahan) && detailModal.fasilitas_tambahan.length > 0 && (
                                                <div className="md:col-span-2 p-3 bg-gray-50 rounded-lg">
                                                    <p className="text-xs text-gray-500 mb-2">Fasilitas Tambahan</p>
                                                    <div className="flex flex-wrap gap-1">
                                                        {detailModal.fasilitas_tambahan.map((f, i) => (
                                                            <span key={i} className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">
                                                                {f}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {detailModal.kebutuhan_khusus && (
                                                <div className="md:col-span-2 p-3 bg-gray-50 rounded-lg">
                                                    <p className="text-xs text-gray-500">Kebutuhan Khusus</p>
                                                    <p className="text-sm">{detailModal.kebutuhan_khusus}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Dokumen Pendukung */}
                                    <div>
                                        <h4 className="font-bold text-[#0F4C3A] mb-3 flex items-center">
                                            <FaFileAlt className="mr-2 text-[#2E7D32]" /> Dokumen Pendukung
                                        </h4>
                                        <div className="space-y-2">
                                            {[
                                                { label: 'KTP', path: detailModal.file_ktp },
                                                { label: 'Surat Pernyataan', path: detailModal.file_surat_pernyataan },
                                                { label: 'Dokumen Pendukung', path: detailModal.file_dokumen_pendukung },
                                            ].filter(f => f.path).map((f, i) => (
                                                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        {getFileIcon(f.path)}
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium text-gray-700">{f.label}</p>
                                                            <p className="text-xs text-gray-500 truncate">{getFileName(f.path)}</p>
                                                        </div>
                                                    </div>
                                                    <a
                                                        href={getFileUrl(f.path)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-2 text-[#2E7D32] hover:bg-green-50 rounded-lg"
                                                        title="Download"
                                                    >
                                                        <FaDownload size={14} />
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Pembayaran */}
                                    {(detailModal.payment_proof || detailModal.payment_note || detailModal.payment_date) && (
                                        <div>
                                            <h4 className="font-bold text-[#0F4C3A] mb-3 flex items-center">
                                                <FaMoneyBillWave className="mr-2 text-[#2E7D32]" /> Info Pembayaran
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {detailModal.payment_date && (
                                                    <div className="p-3 bg-gray-50 rounded-lg">
                                                        <p className="text-xs text-gray-500">Tanggal Bayar</p>
                                                        <p className="text-sm font-semibold">
                                                            {formatTanggal(detailModal.payment_date)}
                                                        </p>
                                                    </div>
                                                )}
                                                {detailModal.verified_at && (
                                                    <div className="p-3 bg-gray-50 rounded-lg">
                                                        <p className="text-xs text-gray-500">Diverifikasi Pada</p>
                                                        <p className="text-sm font-semibold">
                                                            {formatDate(detailModal.verified_at)}
                                                        </p>
                                                    </div>
                                                )}
                                                {detailModal.payment_note && (
                                                    <div className="md:col-span-2 p-3 bg-gray-50 rounded-lg">
                                                        <p className="text-xs text-gray-500">Catatan Pembayaran</p>
                                                        <p className="text-sm whitespace-pre-wrap">{detailModal.payment_note}</p>
                                                    </div>
                                                )}
                                                {detailModal.payment_proof && (
                                                    <div className="md:col-span-2 flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                        <div className="flex items-center gap-3">
                                                            {getFileIcon(detailModal.payment_proof)}
                                                            <div>
                                                                <p className="text-sm font-medium">Bukti Pembayaran</p>
                                                                <p className="text-xs text-gray-500 truncate">
                                                                    {getFileName(detailModal.payment_proof)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <a
                                                            href={getFileUrl(detailModal.payment_proof)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-2 text-[#2E7D32] hover:bg-green-50 rounded-lg"
                                                            title="Lihat"
                                                        >
                                                            <FaDownload size={14} />
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Catatan Admin */}
                                    {detailModal.catatan_admin && (
                                        <div>
                                            <h4 className="font-bold text-[#0F4C3A] mb-3 flex items-center">
                                                <FaInfoCircle className="mr-2 text-[#2E7D32]" /> Catatan Admin
                                            </h4>
                                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                                <p className="text-sm whitespace-pre-wrap">{detailModal.catatan_admin}</p>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Action Footer */}
                        {!detailLoading && detailModal && hasPermission('sewa-gedung.update') && (
                            <div className="p-4 border-t bg-gray-50 rounded-b-2xl flex flex-wrap justify-between items-center gap-3">
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        onClick={() => openStatusModal(detailModal, 'diproses')}
                                        className="px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm flex items-center gap-1 hover:bg-yellow-600"
                                    >
                                        <FaClock size={12} /> Proses
                                    </button>
                                    <button
                                        onClick={() => openStatusModal(detailModal, 'disetujui')}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm flex items-center gap-1 hover:bg-green-700"
                                    >
                                        <FaCheck size={12} /> Setujui
                                    </button>
                                    <button
                                        onClick={() => openStatusModal(detailModal, 'ditolak')}
                                        className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm flex items-center gap-1 hover:bg-red-600"
                                    >
                                        <FaBan size={12} /> Tolak
                                    </button>
                                    <button
                                        onClick={() => openPaymentModal(detailModal)}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm flex items-center gap-1 hover:bg-blue-700"
                                    >
                                        <FaMoneyBillWave size={12} /> Pembayaran
                                    </button>
                                </div>
                                <button
                                    onClick={() => setDetailModal(null)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-white"
                                >
                                    Tutup
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ============ MODAL PAYMENT ============ */}
            {paymentModal && (
                <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-[#0F4C3A]">
                                Konfirmasi Pembayaran
                            </h3>
                            <button onClick={() => setPaymentModal(null)} className="text-gray-400 hover:text-gray-600">
                                <FaTimes size={20} />
                            </button>
                        </div>

                        <p className="text-sm text-gray-600 mb-4">
                            No. Registrasi: <strong className="font-mono">{paymentModal.nomor_registrasi}</strong>
                        </p>

                        <form onSubmit={handlePaymentSubmit} className="space-y-4">
                            {/* Total biaya */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Total Biaya (Rp) <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    value={paymentForm.total_biaya}
                                    onChange={(e) => setPaymentForm(prev => ({ ...prev, total_biaya: e.target.value }))}
                                    min="0"
                                    step="1000"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                    placeholder="Contoh: 5000000"
                                    required
                                />
                                {paymentForm.total_biaya && (
                                    <p className="text-xs text-green-600 mt-1">
                                        = {formatRupiah(paymentForm.total_biaya)}
                                    </p>
                                )}
                            </div>

                            {/* Payment status */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Status Pembayaran
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {Object.entries(paymentStatusConfig).map(([key, config]) => (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => setPaymentForm(prev => ({ ...prev, payment_status: key }))}
                                            className={`py-2.5 px-3 rounded-lg border-2 text-xs font-medium transition-colors ${paymentForm.payment_status === key
                                                    ? 'border-[#2E7D32] bg-green-50 text-[#2E7D32]'
                                                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                                }`}
                                        >
                                            {config.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Payment date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Tanggal Pembayaran
                                </label>
                                <input
                                    type="date"
                                    value={paymentForm.payment_date}
                                    onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                />
                            </div>

                            {/* Payment proof */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Bukti Pembayaran (Opsional)
                                </label>
                                <label className="flex items-center justify-between p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#2E7D32] transition-colors">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <FaUpload className="text-gray-400" />
                                        <span className="text-sm text-gray-600 truncate">
                                            {paymentForm.payment_proof ? paymentForm.payment_proof.name : 'Pilih file...'}
                                        </span>
                                    </div>
                                    <input
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={handlePaymentProofChange}
                                        className="hidden"
                                    />
                                </label>
                                <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG • Maks 5MB</p>
                            </div>

                            {/* Payment note */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Catatan Pembayaran
                                </label>
                                <textarea
                                    value={paymentForm.payment_note}
                                    onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_note: e.target.value }))}
                                    rows="3"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                    placeholder="Catatan untuk pemohon (opsional)"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => setPaymentModal(null)}
                                    className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Batal
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-5 py-2.5 bg-[#2E7D32] text-white rounded-lg font-semibold hover:bg-[#1B5E20] disabled:opacity-50 flex items-center gap-2"
                                >
                                    {submitting ? (
                                        <>
                                            <FaSpinner className="animate-spin" /> Menyimpan...
                                        </>
                                    ) : (
                                        <>
                                            <FaCheckCircle /> Simpan
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ============ MODAL STATUS ============ */}
            {statusModal && (
                <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6">
                        <h3 className="text-lg font-bold text-[#0F4C3A] mb-4">
                            Update Status Permohonan
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            No. Registrasi: <strong className="font-mono">{statusModal.nomor_registrasi}</strong>
                        </p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Status Baru</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {Object.entries(statusConfig).map(([key, config]) => {
                                        const Icon = config.icon;
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => setStatusAction(key)}
                                                className={`py-3 px-4 rounded-lg border-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${statusAction === key
                                                        ? 'border-[#2E7D32] bg-green-50 text-[#2E7D32]'
                                                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                                    }`}
                                            >
                                                <Icon size={14} />
                                                {config.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Catatan Admin {statusAction === 'ditolak' && <span className="text-red-500">*</span>}
                                </label>
                                <textarea
                                    value={statusCatatan}
                                    onChange={(e) => setStatusCatatan(e.target.value)}
                                    rows="3"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                    placeholder={
                                        statusAction === 'ditolak'
                                            ? 'Jelaskan alasan penolakan...'
                                            : 'Catatan untuk pemohon (opsional)'
                                    }
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setStatusModal(null)}
                                className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleUpdateStatus}
                                disabled={submitting || (statusAction === 'ditolak' && !statusCatatan.trim())}
                                className="px-5 py-2.5 bg-[#2E7D32] text-white rounded-lg font-semibold hover:bg-[#1B5E20] disabled:opacity-50 flex items-center gap-2"
                            >
                                {submitting ? (
                                    <>
                                        <FaSpinner className="animate-spin" /> Menyimpan...
                                    </>
                                ) : (
                                    <>
                                        <FaCheckCircle /> Simpan
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============ DELETE CONFIRM ============ */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaTrash className="text-3xl text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Hapus Permohonan?</h3>
                        <p className="text-gray-600 mb-6">
                            Hapus permohonan <strong>{deleteConfirm.nomor_registrasi}</strong> milik{' '}
                            <strong>{deleteConfirm.nama_lengkap}</strong>? Tindakan ini tidak dapat dibatalkan.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-6 py-2.5 border rounded-lg hover:bg-gray-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenPermohonanSewaGedung;